"use client";

import { upload } from "@vercel/blob/client";
import {
  isLocalDevHostname,
  shouldUseStudioMultipartUpload,
  type StudioUploadPurpose,
} from "@/utils/studioBlobPath";
import {
  getStudioAssetPublicUrl,
  STUDIO_BLOB_ACCESS,
} from "@/utils/studioBlobStorage";
import {
  buildStudioUploadTarget,
  type StudioUploadResult,
} from "@/utils/studioUploadShared";

async function uploadStudioFileViaServer(
  pageId: string,
  file: File,
  resourceId?: string,
  purpose: StudioUploadPurpose = "page-file",
): Promise<StudioUploadResult> {
  const body = new FormData();
  body.append("file", file);
  body.append("pageId", pageId);
  body.append("uploadPurpose", purpose);
  if (resourceId) {
    body.append("resourceId", resourceId);
  }

  const response = await fetch("/api/studio/upload", {
    method: "POST",
    body,
  });
  const payload = (await response.json()) as StudioUploadResult & {
    error?: string;
  };

  if (!response.ok || !payload.url || !payload.resourceId || !payload.filename) {
    throw new Error(payload.error ?? "upload_failed");
  }

  return {
    url: payload.url,
    pathname: payload.pathname,
    resourceId: payload.resourceId,
    filename: payload.filename,
  };
}

export async function uploadStudioFileFromClient(
  pageId: string,
  file: File,
  resourceId?: string,
  purpose: StudioUploadPurpose = "page-file",
): Promise<StudioUploadResult> {
  const target = buildStudioUploadTarget(
    pageId,
    file.name,
    file.size,
    resourceId,
    purpose,
  );

  if (
    typeof window !== "undefined" &&
    isLocalDevHostname(window.location.hostname)
  ) {
    return uploadStudioFileViaServer(
      pageId,
      file,
      target.resourceId,
      purpose,
    );
  }

  const blob = await upload(target.pathname, file, {
    access: STUDIO_BLOB_ACCESS,
    handleUploadUrl: "/api/studio/upload",
    multipart: shouldUseStudioMultipartUpload(
      file.size,
      typeof window !== "undefined" ? window.location.hostname : undefined,
    ),
    clientPayload: JSON.stringify({
      pageId: target.pageId,
      resourceId: target.resourceId,
      filename: target.filename,
      fileSize: target.fileSize,
      uploadPurpose: purpose,
    }),
  });

  return {
    url: getStudioAssetPublicUrl(target.pathname),
    pathname: blob.pathname,
    resourceId: target.resourceId,
    filename: target.filename,
  };
}
